import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class Accordion implements Plugin {
  private readonly toggleButtons: NodeListOf<HTMLElement>;
  private readonly contents: NodeListOf<HTMLElement>;
  private readonly expandedClass: string;
  private readonly toggleSelector: string;
  private readonly contentSelector: string;
  private readonly closeOthers: boolean;
  private readonly closeOnBackdropClick: boolean;
  private readonly handleFocusOut: (event: FocusEvent) => void;
  /** `display` que la hoja de estilos da a cada panel, para restaurarlo al abrir. */
  private readonly openDisplay = new WeakMap<HTMLElement, string>();

  constructor(private readonly element: HTMLElement) {
    this.toggleSelector =
      element.dataset.accordionToggleSelector || "[data-accordion-toggle]";
    this.contentSelector =
      element.dataset.accordionContentSelector || "[data-accordion-content]";
    this.expandedClass =
      element.dataset.accordionExpandedClass || "is-expanded";
    this.closeOthers = element.dataset.accordionCloseOthers === "true";
    this.closeOnBackdropClick =
      element.dataset.accordionCloseOnBackdropClick === "true";

    this.toggleButtons = element.querySelectorAll<HTMLElement>(
      this.toggleSelector,
    );
    this.contents = element.querySelectorAll<HTMLElement>(this.contentSelector);

    this.handleFocusOut = () => {
      // Pequeño retraso para permitir que el foco se mueva al siguiente elemento
      setTimeout(() => {
        const active = document.activeElement as HTMLElement | null;
        if (!active || !this.element.contains(active)) {
          this.closeAll();
        }
      }, 0);
    };
  }

  init(): void {
    if (this.toggleButtons.length === 0 || this.contents.length === 0) {
      console.warn(
        "Accordion plugin: No toggles or contents found",
        this.element,
      );
      return;
    }

    this.initializeState();
    this.bindEvents();

    if (!this.element.classList.contains(this.expandedClass)) return;

    // Caso histórico de checkout B2C: el panel necesita `inline-flex`.
    const contentDefault = this.element.querySelector<HTMLElement>(
      ".chk-card-content-b2c",
    );

    if (contentDefault) {
      contentDefault.style.display = "inline-flex";
      contentDefault.setAttribute("aria-hidden", "false");
    }

    // Resto de paneles: se devuelve el display a su valor de hoja de estilos.
    this.contents.forEach((content) => {
      if (content === contentDefault) return;
      content.style.display = this.openDisplay.get(content) ?? "block";
      content.setAttribute("aria-hidden", "false");
    });

    this.toggleButtons.forEach((button) => {
      button.setAttribute("aria-expanded", "true");
    });
  }

  private initializeState(): void {
    this.contents.forEach((content) => {
      const cssDisplay = window.getComputedStyle(content).display;
      this.openDisplay.set(
        content,
        cssDisplay && cssDisplay !== "none" ? cssDisplay : "block",
      );
      content.style.display = "none";
      content.setAttribute("aria-hidden", "true");
    });

    this.toggleButtons.forEach((button) => {
      button.setAttribute("aria-expanded", "false");
    });
  }

  private bindEvents(): void {
    this.toggleButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggle(button);
      });

      button.addEventListener("keydown", (e) => {
        const key = e.key;

        if (key === "Enter" || key === " ") {
          e.preventDefault();
          this.toggle(button);
          return;
        }

        const buttons = Array.from(this.toggleButtons);
        const index = buttons.indexOf(button);
        if (index === -1) return;

        // Si solo hay un botón de acordeón en este contenedor,
        // usamos las flechas para alternar abrir/cerrar en lugar de mover el foco.
        if (buttons.length === 1) {
          if (
            key === "ArrowDown" ||
            key === "ArrowUp" ||
            key === "ArrowLeft" ||
            key === "ArrowRight"
          ) {
            e.preventDefault();
            this.toggle(button);
          }
          return;
        }

        let targetIndex = index;

        if (key === "ArrowDown" || key === "ArrowRight") {
          targetIndex = (index + 1) % buttons.length;
        } else if (key === "ArrowUp" || key === "ArrowLeft") {
          targetIndex = (index - 1 + buttons.length) % buttons.length;
        } else if (key === "Home") {
          targetIndex = 0;
        } else if (key === "End") {
          targetIndex = buttons.length - 1;
        } else {
          return;
        }

        e.preventDefault();
        const targetButton = buttons[targetIndex];
        targetButton?.focus();
      });

      if (!button.hasAttribute("tabindex")) {
        button.setAttribute("tabindex", "0");
      }
    });

    // Navegación por teclado dentro de listas (role="list" / role="listitem")
    const lists = this.element.querySelectorAll<HTMLElement>('[role="list"]');
    lists.forEach((list) => {
      const items = Array.from(
        list.querySelectorAll<HTMLElement>('[role="listitem"]'),
      );

      items.forEach((item, index) => {
        item.addEventListener("keydown", (e: KeyboardEvent) => {
          const key = e.key;

          if (
            key !== "ArrowDown" &&
            key !== "ArrowUp" &&
            key !== "ArrowLeft" &&
            key !== "ArrowRight" &&
            key !== "Home" &&
            key !== "End"
          ) {
            return;
          }

          const lastIndex = items.length - 1;
          let targetIndex = index;

          if (key === "ArrowDown" || key === "ArrowRight") {
            targetIndex = Math.min(index + 1, lastIndex);
          } else if (key === "ArrowUp" || key === "ArrowLeft") {
            targetIndex = Math.max(index - 1, 0);
          } else if (key === "Home") {
            targetIndex = 0;
          } else if (key === "End") {
            targetIndex = lastIndex;
          }

          if (targetIndex !== index) {
            e.preventDefault();
            const target = items[targetIndex];
            target?.focus();
          }
        });
      });
    });

    // Cerrar el acordeón cuando el foco salga del contenedor
    // this.element.addEventListener("focusout", this.handleFocusOut);

    // Cerrar al hacer clic en el overlay de fondo generado por el propio
    // elemento (::before). El target coincide con `this.element` porque los
    // clics sobre contenido generado se reportan sobre el elemento que lo genera.
    if (this.closeOnBackdropClick) {
      this.element.addEventListener("click", (e: Event) => {
        if (e.target === this.element) {
          this.closeAll();
        }
      });
    }

    // Permitir cerrar con Escape desde cualquier elemento interno
    this.element.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Esc") {
        const hasOpen = Array.from(this.contents).some(
          (content) => content.style.display !== "none",
        );

        if (hasOpen) {
          e.stopPropagation();
          this.closeAll();

          const firstToggle = this.toggleButtons[0];
          if (firstToggle) {
            firstToggle.focus();
          }
        }
      }
    });
  }

  /**
   * Toggle por botón, ahora soporta:
   * - botón con index → comportamiento legacy
   * - botón con data-view → contenido dinámico
   */
  private toggle(button: HTMLElement): void {
    // Detectamos si tiene data-view
    const viewSelector = button.dataset.view;
    let targetContent: HTMLElement | undefined;

    if (viewSelector) {
      targetContent =
        this.element.querySelector<HTMLElement>(viewSelector) || undefined;
    } else {
      // fallback legacy: índice según toggleButtons
      const index = Array.from(this.toggleButtons).indexOf(button);
      targetContent = this.contents[index];
    }

    if (!targetContent) return;

    const isExpanded = targetContent.style.display !== "none";

    if (this.closeOthers) {
      this.closeAll();
    }

    if (isExpanded) {
      this.closeContent(targetContent, button);
    } else {
      this.openContent(targetContent, button);
    }
  }

  private openContent(content: HTMLElement, button: HTMLElement): void {
    this.element.classList.add(this.expandedClass);
    content.style.display = this.openDisplay.get(content) ?? "block";
    content.setAttribute("aria-hidden", "false");
    button.setAttribute("aria-expanded", "true");

    this.dispatchEvent("accordionOpened", content);
  }

  private closeContent(content: HTMLElement, button: HTMLElement): void {
    this.element.classList.remove(this.expandedClass);
    content.style.display = "none";
    content.setAttribute("aria-hidden", "true");
    button.setAttribute("aria-expanded", "false");

    this.dispatchEvent("accordionClosed", content);
  }

  private closeAll(): void {
    this.contents.forEach((content) => {
      content.style.display = "none";
      content.setAttribute("aria-hidden", "true");
    });
    this.toggleButtons.forEach((button) => {
      button.setAttribute("aria-expanded", "false");
    });
    this.element.classList.remove(this.expandedClass);
  }

  private dispatchEvent(name: string, content: HTMLElement): void {
    const index = Array.from(this.contents).indexOf(content);
    const toggle = this.toggleButtons[index] || null;

    this.element.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          index,
          toggle,
          content,
        },
      }),
    );
  }
}

PluginRegistry.register("accordion", Accordion);
